import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

export type Theme = 'light' | 'dark' | 'colorful'

// Pinned set so ``readStoredTheme`` can validate without listing each value.
// Order is the cycle order ThemeSwitcher steps through.
const THEMES: readonly Theme[] = ['light', 'dark', 'colorful'] as const

const STORAGE_KEY = 'theme'

type ThemeContextValue = {
  theme: Theme
  setTheme: (t: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  // Defensive coerce: anything not in the THEMES set (stale "old-theme",
  // hand-edited value, etc.) falls back to light. The cast is safe
  // because of the includes guard.
  return THEMES.includes(stored as Theme) ? (stored as Theme) : 'light'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)

  useEffect(() => {
    // ``light`` is the default and uses the bare :root variables, so we
    // REMOVE the attribute entirely (the CSS treats absence as default).
    // ``dark`` and ``colorful`` set the attribute so [data-theme="..."]
    // selectors match.
    if (theme === 'light') {
      document.documentElement.removeAttribute('data-theme')
    } else {
      document.documentElement.setAttribute('data-theme', theme)
    }
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next)
    setThemeState(next)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
